import { AutocompleteResult } from 'react-select';
import { School } from '../domain/School';
import { SchoolOption } from '../domain/SchoolOption';
import { text } from '../common/text';

/**
 * Result zur Anzeige von Optionen im Suchfeld
 */
export class OptionsAutocompleteResult implements AutocompleteResult<number> {
  constructor(public options: SchoolOption[], public complete: boolean = true) {}
}

/**
 * Result zur Anzeige von Schulen im Suchfeld
 */
export class SchoolsAutocompleteResult implements AutocompleteResult<number> {
  public options: SchoolOption[];
  public complete: boolean;

  constructor(schools: School[], searchTerm: string) {
    this.options = schools.map((school: School) => mapSchool(school, searchTerm)).sort(sortSchools);

    this.complete = true;
  }
}

/**
 * Wandelt eine Schule in eine SchulOption um
 * und setzt dabei den SuchIndex in Schulnamen und -Adresse
 *
 * @param {School} Schule
 * @returns {SchoolOption}
 */
export const mapSchool = (
  { schoolId, displayName, loginName, address, server, serverUrl, webuntisBaseUrl }: School,
  searchTerm: string,
): SchoolOption => ({
  id: schoolId,
  label: displayName,
  subLabel: address,

  // wenn das server api die property serverUrl zurückschickt nehmen wir diese, ansonsten basteln wir selbst
  url: serverUrl || webuntisBaseUrl,

  // Wo genau wurde der Suchbegriff gefunden?
  lI: displayName.toLowerCase().indexOf(searchTerm.toLowerCase()),
  sLI: address.toLowerCase().indexOf(searchTerm.toLowerCase()),
  sLength: searchTerm.length,
});

/**
 * Sortiert zwei Schulen nach SuchIndex
 *
 * @param {SchoolOption} s1
 * @param {SchoolOption} s2
 * @returns {number}
 */
export const sortSchools = (s1: SchoolOption, s2: SchoolOption): number => {
  // Treffer im Schulnamen
  const foundInS1N: boolean = s1.lI > -1;
  const foundInS2N: boolean = s2.lI > -1;

  // Treffer in der Addresse
  const foundInS1A: boolean = s1.sLI > -1;
  const foundInS2A: boolean = s2.sLI > -1;

  // Ergebniss sollen erst nach Treffer im Schulnamen...
  if (foundInS1N && !foundInS2N) {
    return -1;
  } else if (!foundInS1N && foundInS2N) {
    return 1;
  } else if (foundInS1N && foundInS2N) {
    if (s1.lI < s2.lI) {
      return -1;
    } else if (s1.lI > s2.lI) {
      return 1;
    }
  }
  // dann nach Treffer in der Adresszeile...
  if (foundInS1A && !foundInS2A) {
    return -1;
  } else if (!foundInS1A && foundInS2A) {
    return 1;
  } else if (foundInS1A && foundInS2A) {
    if (s1.sLI < s2.sLI) {
      return -1;
    } else if (s1.sLI > s2.sLI) {
      return 1;
    }
  }
  // und zuletzt alphabetisch gereiht werden.
  if (s1.label && s2.label) {
    if (s1.label < s2.label) {
      return -1;
    } else if (s1.label > s2.label) {
      return 1;
    }
  }
  if (s1.subLabel && s2.subLabel) {
    if (s1.subLabel < s2.subLabel) {
      return -1;
    } else if (s1.subLabel > s2.subLabel) {
      return 1;
    }
  }

  return 0;
};

/**
 * Suchergebnis wenn erst drei Buchstaben eingetippt wurden
 *
 * @type {OptionsAutocompleteResult}
 */
export const EMPTY = new OptionsAutocompleteResult([]);

/**
 * Suchergebnis "Zu viele Schulen"
 *
 * @type {OptionsAutocompleteResult}
 */
export const TOO_MANY = new OptionsAutocompleteResult([
  {
    id: -1,
    label: text('LBL_TOO_MANY_SCHOOLS'),
    subLabel: text('LBL_REFINE_SEARCH'),

    /**
     * Disablen damit die Option nicht angeklickt werden kann
     */
    disabled: true,
  },
]);

/**
 * Leeres Suchergebnis
 *
 * @type {OptionsAutocompleteResult}
 */
export const NOT_FOUND = (UNTIS_URL: string) =>
  new OptionsAutocompleteResult([
    {
      id: -2,
      label: text('LBL_SCHOOL_NOT_FOUND'),
      subLabel: text('LBL_CONTACT_SCHOOL_ADMIN'),

      url: UNTIS_URL,

      className: 'link',
    },
  ]);



// WEBPACK FOOTER //
// ./src/app/appServiceResult.ts